import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import { useProperty } from '../context/PropertyContext';
import useLanguage from '../hooks/useLanguage';
import { usePricingGuidance } from '../hooks/usePricingGuidance';
import { secureApiService } from '../services/secureApi';
import DesktopLayout from '../layouts/DesktopLayout';
import AccountSwitcher from '../components/common/AccountSwitcher';
import PropertySwitcher from '../components/common/PropertySwitcher';
import PricingScoreboard from '../components/pricing/PricingScoreboard';
import WeekdayStrip from '../components/pricing/WeekdayStrip';
import ThirtyDayTable from '../components/pricing/ThirtyDayTable';
import QuoteBox from '../components/pricing/QuoteBox';
import AirbnbOffersCard from '../components/pricing/AirbnbOffersCard';
import PolicySettings from '../components/pricing/PolicySettings';
import '../components/pricing/pricing-guidance.css';

/**
 * Pricing Guidance — ops-only, one unit at a time.
 *
 * GUIDANCE, not recommendations. The page shows what a unit actually achieves and lets
 * ops quote a stay honestly. It does NOT suggest a price, and it never writes one: the
 * published rate is changed in the property calendar and syncs to Channex from there.
 * That seam (evidence → human decision → published rate → channel sync) is deliberate,
 * and it is what would make a future automation phase a swap rather than a rewrite.
 *
 * Replaces the recommendations page, whose portfolio-wide counter read "1" across the
 * estate and trained ops to ignore the screen.
 * See tasks/pricing-guidance-phase1.md for the 24 locked decisions behind it.
 */
const PricingGuidancePage = () => {
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useAccount();
  const { selectedProperty, setSelectedProperty } = useProperty();
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const [activeNavItem, setActiveNavItem] = useState('pricing-assistant');

  // `?property=<id>` — a link from another page (the «التقويم» night popup) opens on that unit. Read
  // once into the unit picker, then dropped from the address, so the picker alone decides from there
  // (a later pick is not undone by a refresh). An id the picker does not list is cleared by it.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedUnit = searchParams.get('property');
  useEffect(() => {
    if (!linkedUnit) return;
    setSelectedProperty(linkedUnit);
    setSearchParams(params => {
      params.delete('property');
      return params;
    }, { replace: true });
  }, [linkedUnit, setSelectedProperty, setSearchParams]);

  // The account filter has to reach the query, not just the screen. Rendering the
  // switcher without passing `ownerId` gives ops a control that visibly changes and
  // silently does nothing — worse than not offering one. The account is part of the
  // cache key for the same reason: two owners must never share one cached unit list.
  const propertiesQuery = useQuery({
    queryKey: ['properties', 'list', selectedAccount || 'all'],
    queryFn: () => secureApiService.getProperties(selectedAccount ? { ownerId: selectedAccount } : {})
  });

  // The endpoint has returned both a bare array and a wrapped object over its life;
  // read defensively rather than assume today's shape.
  const properties = useMemo(() => {
    const payload = propertiesQuery.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.properties)) return payload.properties;
    return [];
  }, [propertiesQuery.data]);

  const guidance = usePricingGuidance(selectedProperty);

  // Every pre-filled date comes from the server. A browser-computed default pre-fills
  // YESTERDAY between midnight and 03:00 Riyadh — the page would open quoting a stay
  // in the past and nothing on screen would say so.
  const defaultCheckIn = guidance.data?.days?.[0]?.date;
  const defaultCheckOut = guidance.data?.days?.[1]?.date;

  const openCalendarAt = (date) => {
    navigate(`/properties/${selectedProperty}?tab=calendar&date=${date}`);
  };

  const isOps = user?.role === 'admin' || user?.role === 'ops';

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle={t('pricing.page.title')}
    >
      <div className="pg" dir={isArabic ? 'rtl' : 'ltr'}>
        {isOps && (
          <div style={{ marginBottom: '1.25rem' }}>
            <AccountSwitcher />
          </div>
        )}

        <div className="pg-block__head" style={{ marginBottom: '1rem' }}>
          <h1 className="pg-block__title" style={{ fontSize: '1.35rem' }}>
            <i className="bi bi-calculator" />
            {t('pricing.page.title')}
          </h1>
          <p className="pg-block__hint">
            {t('pricing.page.subtitle')}
            {' · '}
            {t('pricing.page.opsBadge')}
          </p>
          <p className="pg-block__hint" style={{ marginTop: 2 }}>
            <i className="bi bi-info-circle" style={{ marginInlineEnd: 4 }} />
            {t('pricing.page.readOnly')}
          </p>
        </div>

        {/* Mounted only once the list has arrived. PropertySwitcher validates the stored
            selection against `properties` on every render — handed an empty array during
            the load it clears the session's chosen unit, so the page silently reset to the
            first unit on every refresh. Same guard RevenueOptimizationDashboard uses. */}
        {properties.length > 0 && <PropertySwitcher properties={properties} />}

        {propertiesQuery.isError && (
          <div className="pg-block">
            <div className="pg-notice pg-notice--blocked">{t('pricing.page.loadError')}</div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => propertiesQuery.refetch()}
            >
              {t('pricing.page.retry')}
            </button>
          </div>
        )}

        {/* A FAILED fetch is not an empty estate. Without the error branch above, a 500
            on /properties rendered a confident "no units available". */}
        {!propertiesQuery.isLoading && !propertiesQuery.isError && properties.length === 0 && (
          <div className="pg-block">{t('pricing.page.noUnits')}</div>
        )}

        {selectedProperty && guidance.isLoading && (
          <div className="pg-block text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('pricing.page.loading')}</span>
            </div>
          </div>
        )}

        {selectedProperty && guidance.isError && (
          <div className="pg-block">
            <div className="pg-notice pg-notice--blocked">{t('pricing.page.loadError')}</div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => guidance.refetch()}
            >
              {t('pricing.page.retry')}
            </button>
          </div>
        )}

        {guidance.data && (
          <>
            <PricingScoreboard scoreboard={guidance.data.scoreboard} byChannel={guidance.data.byChannel} />
            <WeekdayStrip weekday={guidance.data.weekday} />
            {/* Above the quote on purpose: ops see what Airbnb is already giving before they quote
                or offer more. Own query and own error state — it never blocks the page. */}
            <AirbnbOffersCard key={`offers-${selectedProperty}`} propertyId={selectedProperty} />
            <QuoteBox
              // Remounts on unit change so a quote can never outlive the unit it was
              // priced for and sit under the next unit's scoreboard.
              key={selectedProperty}
              propertyId={selectedProperty}
              defaultCheckIn={defaultCheckIn}
              defaultCheckOut={defaultCheckOut}
              floor={guidance.data.policy?.floor}
            />
            <ThirtyDayTable days={guidance.data.days} onOpenDate={openCalendarAt} />
            <PolicySettings
              key={`policy-${selectedProperty}`}
              propertyId={selectedProperty}
              policy={guidance.data.policy}
            />
          </>
        )}
      </div>
    </DesktopLayout>
  );
};

export default PricingGuidancePage;
